import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Plus } from "lucide-react";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import AppBookingItem from "@/components/app/AppBookingItem";
import { useAppMode } from "@/hooks/useAppMode";
import { useBookings } from "@/hooks/useBookings";

const AppBookings = () => {
  const navigate = useNavigate();
  const { provider } = useAppMode();
  const { data: bookings = [], isLoading } = useBookings();
  const [tab, setTab] = useState("upcoming");

  const providerBookings = bookings.filter(
    (b) => b.provider_id === provider?.id
  );

  const now = new Date();

  const upcoming = providerBookings.filter((b) => {
    const status = b.status.toLowerCase();
    if (status === "cancelled" || status === "completed") return false;
    const [h, m] = b.end_time.split(":").map(Number);
    const dt = new Date(b.booking_date);
    dt.setHours(h, m, 0, 0);
    return dt >= now;
  });

  const past = providerBookings.filter((b) => {
    const status = b.status.toLowerCase();
    if (status === "cancelled" || status === "completed") return true;
    const [h, m] = b.end_time.split(":").map(Number);
    const dt = new Date(b.booking_date);
    dt.setHours(h, m, 0, 0);
    return dt < now;
  });

  return (
    <MobileAppLayout title="Your bookings">
      <div className="px-4 py-5">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold tracking-tight">Bookings</h1>
          <Button size="sm" onClick={() => navigate("/app/book")}>
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full mb-4">
            <TabsTrigger value="upcoming">
              Upcoming ({upcoming.length})
            </TabsTrigger>
            <TabsTrigger value="past">Past ({past.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="space-y-3 mt-0">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : upcoming.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    No upcoming bookings.
                  </p>
                  <Button onClick={() => navigate("/app/book")}>
                    <Plus className="h-4 w-4 mr-2" />
                    Book a session
                  </Button>
                </CardContent>
              </Card>
            ) : (
              upcoming.map((b) => (
                <AppBookingItem key={b.id} booking={b} />
              ))
            )}
          </TabsContent>

          <TabsContent value="past" className="space-y-3 mt-0">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : past.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-sm text-muted-foreground">
                  No past bookings yet.
                </CardContent>
              </Card>
            ) : (
              past.map((b) => (
                <AppBookingItem key={b.id} booking={b} />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MobileAppLayout>
  );
};

export default AppBookings;
